import React, { useState, useEffect } from 'react';
import { X, Plus, DollarSign, Users, Search } from 'lucide-react';
import api from '../services/api';

interface User {
  id: string;
  _id?: string; // 하위 호환성을 위해 유지
  nickname?: string;
  email?: string;
  points?: {
    balance: number;
    totalEarned: number;
    totalSpent: number;
  };
  createdAt?: string;
}

interface PointManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PointManagementModal: React.FC<PointManagementModalProps> = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pointAmount, setPointAmount] = useState('');
  const [pointReason, setPointReason] = useState('');
  const [isAddingPoints, setIsAddingPoints] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api().get('/admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      } else {
        setError('사용자 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      console.error('Users fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async () => {
    if (!selectedUser || !pointAmount) {
      alert('사용자를 선택하고 포인트 금액을 입력해주세요.');
      return;
    }

    const amount = parseInt(pointAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('올바른 포인트 금액을 입력해주세요.');
      return;
    }

    try {
      setIsAddingPoints(true);
      const response = await api().post('/admin/users/points', {
        userId: selectedUser.id || selectedUser._id,
        amount: amount,
        reason: pointReason || '관리자 지급',
      });

      if (response.data.success) {
        alert('포인트가 성공적으로 지급되었습니다.');
        setPointAmount('');
        setPointReason('');
        setSelectedUser(null);
        fetchUsers(); // 목록 새로고침
      } else {
        alert(response.data.message || '포인트 지급에 실패했습니다.');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || '포인트 지급 중 오류가 발생했습니다.');
      console.error('Add points error:', err);
    } finally {
      setIsAddingPoints(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <DollarSign className="h-6 w-6 text-green-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">포인트 관리</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="사용자 검색 (닉네임 또는 이메일)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Add Points Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">포인트 지급</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">사용자 선택</label>
                  <select
                    value={selectedUser?.id || selectedUser?._id || ''}
                    onChange={(e) => {
                      const user = users.find((u) => u.id === e.target.value || u._id === e.target.value);
                      setSelectedUser(user || null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">사용자를 선택하세요</option>
                    {filteredUsers.map((user) => (
                      <option key={user.id || user._id} value={user.id || user._id}>
                        {user.nickname || user.email} ({user.points?.balance || 0}P)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">포인트 금액</label>
                  <input
                    type="number"
                    value={pointAmount}
                    onChange={(e) => setPointAmount(e.target.value)}
                    placeholder="지급할 포인트"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">사유 (선택사항)</label>
                  <input
                    type="text"
                    value={pointReason}
                    onChange={(e) => setPointReason(e.target.value)}
                    placeholder="포인트 지급 사유"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    onClick={handleAddPoints}
                    disabled={!selectedUser || !pointAmount || isAddingPoints}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    {isAddingPoints ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    포인트 지급
                  </button>
                </div>
              </div>
            </div>

            {/* Users List */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">사용자 목록</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">로딩 중...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-600">{error}</p>
                  <button onClick={fetchUsers} className="mt-2 text-blue-600 hover:text-blue-800">
                    다시 시도
                  </button>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">사용자가 없습니다.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          사용자
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          이메일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          보유 포인트
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          가입일
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <tr
                          key={user.id || user._id}
                          className={`hover:bg-gray-50 cursor-pointer ${
                            (selectedUser?.id || selectedUser?._id) === (user.id || user._id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setSelectedUser(user)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                                <Users className="h-4 w-4 text-gray-600" />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.nickname || '닉네임 없음'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {user.email || '이메일 없음'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="font-semibold text-green-600">{user.points?.balance || 0}P</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.createdAt
                              ? new Date(user.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointManagementModal;
